/**
 * Scheduling Format Utilities
 * 
 * Timezone-aware date/time formatting for the scheduling system.
 * Uses the existing lib/timezones.ts for timezone data.
 */

import { formatInTimezone } from '@/lib/timezones';

export interface FormattedSlotTime {
  date: string;
  time: string;
  formatted: string;
}

/**
 * Format a slot time in the user's timezone
 * Returns date, time, and combined formatted string
 */
export function formatSlotTime(isoTime: string, timezone: string): FormattedSlotTime {
  const date = formatInTimezone(isoTime, timezone, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  
  const time = formatInTimezone(isoTime, timezone, {
    hour: 'numeric',
    minute: '2-digit',
  });
  
  return { 
    date, 
    time, 
    formatted: `${date} at ${time}` 
  };
}

/**
 * Format a date for display (full format)
 */
export function formatSlotDate(isoTime: string, timezone: string): string {
  return formatInTimezone(isoTime, timezone, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a time for display
 */
export function formatSlotTimeOnly(isoTime: string, timezone: string): string {
  return formatInTimezone(isoTime, timezone, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Get timezone abbreviation (e.g., "EST", "PST")
 */
export function getTimezoneAbbr(isoTime: string, timezone: string): string {
  return formatInTimezone(isoTime, timezone, { timeZoneName: 'short' })
    .split(' ')
    .pop() || '';
}

/**
 * Format a list of slots for display with pagination info
 */
export function formatSlotList(
  slots: Array<{ startTime: string; instructorName?: string }>,
  timezone: string,
  offset: number,
  perPage: number,
  total: number
): string {
  const displayed = slots.slice(offset, offset + perPage);
  const hasMore = offset + perPage < total;
  
  const lines = displayed.map((slot, i) => {
    const { date, time } = formatSlotTime(slot.startTime, timezone);
    const instructor = slot.instructorName ? ` with ${slot.instructorName}` : '';
    return `${offset + i + 1}. ${date} at ${time}${instructor}`;
  });
  
  if (hasMore) {
    lines.push(`\nReply "more" to see more options.`);
  }
  
  return lines.join('\n');
}

/**
 * Check if a slot is within business hours (for highlighting)
 */
export function isWithinBusinessHours(
  isoTime: string, 
  timezone: string,
  startHour: number = 7,
  endHour: number = 21
): boolean {
  const hour = parseInt(formatInTimezone(isoTime, timezone, { hour: 'numeric', hour12: false }));
  return hour >= startHour && hour < endHour;
}
